import { useMgPrisConfig } from '@retail/app/stores/app-config';
import { useAuthContext } from '@retail/auth/data-access';
import { buildUrl } from '@shared/utils';
import { mpeApi } from '@retail/mgpris/config';
import { useMPEFetch } from '@retail/app/hooks';
import {
  CompetitorSummaryDTO,
  ConditionPartitionedCountDTO,
  NewAssortmentItemDTO,
} from '@retail/dashboard/types';
import { QueryOptions } from '@shared/fetch-utils';

interface DashboardProps extends QueryOptions {
  assortmentId: number;
  from: string;
  to: string;
}

export function useFetchNewAssortmentItems({
  assortmentId,
  from,
  to,
  ...queryOptions
}: DashboardProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<NewAssortmentItemDTO[]>({
    method: 'GET',
    queryKey: ['dashboard-new-assortment-items', assortmentId, from, to],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.dashboard.newAssortmentItems.getFullLinkPath(),
      searchParams: {
        assortmentId,
        from,
        to,
      },
    }),
    ...queryOptions,
  });
}

export function useFetchUpdatedAgreementPrices({
  assortmentId,
  from,
  to,
  ...queryOptions
}: DashboardProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<ConditionPartitionedCountDTO[]>({
    method: 'GET',
    queryKey: ['updated-agreement-prices', assortmentId, from, to],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.dashboard.updatedAgreementPrices.getFullLinkPath(),
      searchParams: {
        assortmentId,
        from,
        to,
      },
    }),
    ...queryOptions,
  });
}

export function useFetchCompetitorPriceSummary({
  assortmentId,
  from,
  ...queryOptions
}: Omit<DashboardProps, 'to'>) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<CompetitorSummaryDTO[]>({
    method: 'GET',
    queryKey: ['competitor-price-summary', assortmentId, from],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.competitorPrices.assortment.assortmentId.summary.getFullLinkPath({
        assortmentId,
      }),
      searchParams: { fromDate: from },
    }),
    ...queryOptions,
  });
}
